import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { PageImage } from 'src/helpers/pageImageUtils';
import PageImageLightboxDialog from './PageImageLightboxDialog';

interface PageImageLightboxContextValue {
  images: PageImage[];
  openAtSrc: (src: string) => void;
}

const PageImageLightboxContext = createContext<PageImageLightboxContextValue | null>(null);

export function usePageImageLightbox() {
  return useContext(PageImageLightboxContext);
}

interface ProviderProps {
  images: PageImage[];
  children: ReactNode;
}

export function PageImageLightboxProvider({ images, children }: ProviderProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openAtSrc = useCallback(
    (src: string) => {
      const index = images.findIndex(image => image.src === src);
      if (index < 0) return;
      setActiveIndex(index);
      setOpen(true);
    },
    [images],
  );

  const contextValue = useMemo(
    () => (images.length > 0 ? { images, openAtSrc } : null),
    [images, openAtSrc],
  );

  return (
    <PageImageLightboxContext.Provider value={contextValue}>
      {children}
      {images.length > 0 ? (
        <PageImageLightboxDialog
          images={images}
          open={open}
          activeIndex={activeIndex}
          onClose={() => setOpen(false)}
          onIndexChange={setActiveIndex}
        />
      ) : null}
    </PageImageLightboxContext.Provider>
  );
}
